import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FileText,
  Image,
  Maximize2,
  Mic,
  Paperclip,
  Pencil,
  Reply,
  Send,
  Square,
  Trash2,
  Upload,
  Video,
  X,
} from 'lucide-react';
import {
  apiCreateTaskComment,
  apiCreateTaskSubtask,
  apiDeleteTaskAttachment,
  apiDeleteTaskComment,
  apiGetWorkItemDetail,
  apiListTeamMembers,
  apiPatchTaskComment,
  apiPatchTaskSubtask,
  apiSaveWorkItem,
  apiUploadTaskAttachment,
  fetchTaskAttachmentBlobUrl,
  mapTeamMemberDtoToUi,
} from './api/client';
import { sampleMeetingLink, sampleSubtaskTitle } from './lib/sampleData';
import { normalizeUploadFile } from './lib/normalizeUploadFile';
import { TaskDescriptionEditor } from './components/TaskDescriptionEditor';
import { FullscreenMediaOverlay } from './components/FullscreenMediaOverlay';

/**
 * @param {string} text
 * @param {number} cursorPos
 * @param {string} memberDisplayName roster display name (must match server @mention token)
 */
function insertMentionAtCursor(text, cursorPos, memberDisplayName) {
  const before = text.slice(0, cursorPos);
  const after = text.slice(cursorPos);
  const atIndex = before.lastIndexOf('@');
  if (atIndex < 0) return { text, cursor: cursorPos };
  const newBefore = `${before.slice(0, atIndex)}@${memberDisplayName} `;
  return { text: newBefore + after, cursor: newBefore.length };
}

const priorityUi = {
  Critical:
    'bg-red-100 text-red-700 ring-1 ring-red-200 dark:bg-red-900/30 dark:text-red-300 dark:ring-red-700/50',
  High:
    'bg-orange-100 text-orange-700 ring-1 ring-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:ring-orange-700/50',
  Medium:
    'bg-blue-100 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-700/50',
  Low:
    'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-700/50',
};

const priorityTranslationKeys = {
  Critical: 'priorityCritical',
  High: 'priorityHigh',
  Medium: 'priorityMedium',
  Low: 'priorityLow',
};

const columnTranslationKeys = {
  Backlog: 'columnBacklog',
  'To Do': 'columnToDo',
  'In Progress': 'columnInProgress',
  'In Review': 'columnInReview',
  Done: 'columnDone',
};

function priorityLabel(priority, t) {
  return t(priorityTranslationKeys[priority] || priority);
}

function columnLabel(columnName, t) {
  return t(columnTranslationKeys[columnName] || columnName);
}

function pick(o, camel, pascal, fallback = '') {
  const v = o?.[camel] ?? o?.[pascal];
  return v ?? fallback;
}

function pickNum(o, camel, pascal, fallback = 0) {
  const v = pick(o, camel, pascal, null);
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function mapCommentsTree(nodes) {
  if (!Array.isArray(nodes)) return [];
  return nodes.map(mapCommentOne);
}

function mapCommentOne(c) {
  return {
    id: String(pickNum(c, 'id', 'Id')),
    authorUserId: String(pick(c, 'authorUserId', 'AuthorUserId', '')),
    authorName: pick(c, 'authorName', 'AuthorName', ''),
    body: pick(c, 'body', 'Body', ''),
    createdAtUtc: pick(c, 'createdAtUtc', 'CreatedAtUtc', ''),
    replies: mapCommentsTree(c?.replies ?? c?.Replies),
  };
}

function isoDateSlice(utcMaybe) {
  if (utcMaybe == null || utcMaybe === '') return '';
  try {
    return new Date(utcMaybe).toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

function pickRecordingMimeType() {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') return '';
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
    'audio/ogg',
  ];
  return types.find((t) => MediaRecorder.isTypeSupported(t)) || '';
}

function extFromRecorderMime(mime) {
  const m = String(mime || '').toLowerCase();
  if (m.includes('webm')) return '.webm';
  if (m.includes('mp4') || m.includes('m4a')) return '.m4a';
  if (m.includes('ogg')) return '.ogg';
  return '.webm';
}

function formatRecMmSs(totalSec) {
  const s = Math.max(0, Math.floor(totalSec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

function normalizeDetail(raw, taskFallback) {
  if (!raw || typeof raw !== 'object') {
    return {
      id: String(taskFallback.id),
      taskNumber: String(taskFallback.taskNumber || ''),
      columnId: String(taskFallback.columnId),
      title: taskFallback.title || '',
      description: '',
      priority: taskFallback.priority || 'Medium',
      dueDateLocal: typeof taskFallback.dueDateIso === 'string' ? taskFallback.dueDateIso : '',
      assigneeUserId: taskFallback.assigneeUserId ?? taskFallback.assigneeId ?? '',
      meetingLink: '',
      attachments: [],
      subtasks: [],
      commentsTree: [],
    };
  }

  const atts = raw.attachments ?? raw.Attachments ?? [];
  const sts = raw.subtasks ?? raw.Subtasks ?? [];
  const coms = raw.comments ?? raw.Comments ?? [];
  const dueUtc = raw.dueDateUtc ?? raw.DueDateUtc;

  return {
    id: String(pickNum(raw, 'id', 'Id', Number(taskFallback.id))),
    taskNumber: String(pick(raw, 'taskNumber', 'TaskNumber', taskFallback.taskNumber ?? '') || ''),
    columnId: String(pickNum(raw, 'columnId', 'ColumnId', Number(taskFallback.columnId))),
    title: pick(raw, 'title', 'Title', taskFallback.title || ''),
    description: pick(raw, 'description', 'Description', ''),
    priority: pick(raw, 'priority', 'Priority', taskFallback.priority || 'Medium'),
    dueDateLocal:
      isoDateSlice(dueUtc) ||
      (typeof taskFallback.dueDateIso === 'string' ? taskFallback.dueDateIso : '') ||
      '',
    assigneeUserId: pick(raw, 'assigneeUserId', 'AssigneeUserId', '') || '',
    meetingLink: pick(raw, 'meetingLink', 'MeetingLink', '') || '',
    attachments: Array.isArray(atts)
      ? atts.map((a) => ({
          id: String(pickNum(a, 'id', 'Id')),
          originalFileName: pick(a, 'originalFileName', 'OriginalFileName', 'file'),
          contentType: pick(a, 'contentType', 'ContentType', 'application/octet-stream'),
          kind: String(pick(a, 'kind', 'Kind', 'file')).toLowerCase(),
          fileLength: pickNum(a, 'fileLength', 'FileLength', 0),
        }))
      : [],
    subtasks: Array.isArray(sts)
      ? sts.map((s) => ({
          id: String(pickNum(s, 'id', 'Id')),
          title: pick(s, 'title', 'Title', ''),
          done: !!(s.done ?? s.Done),
          sortOrder: pickNum(s, 'sortOrder', 'SortOrder', 0),
        }))
      : [],
    commentsTree: mapCommentsTree(coms),
  };
}

function AttachmentPreview({ apiProjectId, taskId, attachment, onDelete, t }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState('');
  const [lightbox, setLightbox] = useState(null);
  const idKey = attachment.id;

  useEffect(() => {
    let dead = false;
    let revoked = null;
    (async () => {
      try {
        revoked = await fetchTaskAttachmentBlobUrl(apiProjectId, taskId, idKey);
        if (!dead) setUrl(revoked);
      } catch (e) {
        if (!dead) setErr(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      dead = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [apiProjectId, taskId, idKey]);

  const kind = attachment.kind || 'file';
  let body;
  if (err) body = <p className="text-[11px] text-red-600 dark:text-red-400">{err}</p>;
  else if (!url) body = <p className="text-[11px] text-slate-500 dark:text-slate-400">{t('loadingPreview')}</p>;
  else if (kind === 'image')
    body = (
      <button
        type="button"
        className="block w-full rounded-lg border border-brand-100 p-0 text-left dark:border-brand-900"
        onClick={() => setLightbox({ src: url, mediaType: 'image' })}
        aria-label={t('viewImageFullScreen')}
      >
        <img
          src={url}
          alt={attachment.originalFileName}
          className="max-h-56 max-w-full cursor-default rounded-lg object-contain"
        />
      </button>
    );
  else if (kind === 'video')
    body = (
      <div className="relative w-full max-w-xl">
        <video
          src={url}
          controls
          className="max-h-64 w-full rounded-lg border border-brand-100 dark:border-brand-900"
        />
        <button
          type="button"
          aria-label={t('fullScreenVideo')}
          className="absolute bottom-3 right-3 flex items-center justify-center rounded-lg bg-black/70 p-2 text-white shadow backdrop-blur-sm"
          onClick={() => setLightbox({ src: url, mediaType: 'video' })}
        >
          <Maximize2 size={18} strokeWidth={2} />
        </button>
      </div>
    );
  else if (kind === 'audio') body = <audio src={url} controls className="w-full" />;
  else
    body = (
      <a href={url} download={attachment.originalFileName} className="text-xs font-medium text-brand-700 underline dark:text-brand-300">
        {t('download')} {attachment.originalFileName}
      </a>
    );

  return (
    <div className="rounded-lg border border-brand-200 px-3 py-2 dark:border-brand-900">
      <div className="mb-2 flex items-center justify-between gap-2 text-xs font-medium text-slate-700 dark:text-slate-200">
        <span className="flex max-w-[70%] items-center gap-1 truncate">
          <Paperclip size={12} />
          {attachment.originalFileName}
          <span className="font-normal text-slate-400">({kind})</span>
        </span>
        {onDelete && (
          <button
            type="button"
            onClick={() => onDelete(attachment.id)}
            className="shrink-0 rounded px-2 py-0.5 text-[11px] text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
          >
            {t('delete')}
          </button>
        )}
      </div>
      {body}
      <FullscreenMediaOverlay
        open={!!lightbox?.src}
        onClose={() => setLightbox(null)}
        src={lightbox?.src ?? null}
        mediaType={lightbox?.mediaType ?? 'image'}
      />
    </div>
  );
}

function CommentBranch({
  c,
  depth,
  decorateMentions,
  viewerUserId,
  onReply,
  onSaveEdit,
  onDelete,
  replyDraftById,
  setReplyDraftById,
  editingId,
  setEditingId,
  editDraft,
  setEditDraft,
  t,
}) {
  const canEdit = viewerUserId && c.authorUserId === viewerUserId;
  return (
    <div className={`rounded-lg border border-brand-100 p-2.5 dark:border-brand-900/60 ${depth > 0 ? 'mt-2 ml-2 border-l-2 border-brand-200 dark:border-brand-800' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-500 text-[10px] font-semibold text-white">
            {(c.authorName || '?')
              .split(' ')
              .map((w) => w[0])
              .join('')
              .slice(0, 2)}
          </div>
          <div>
            <p className="text-xs font-medium text-slate-700 dark:text-slate-200">{c.authorName}</p>
            <p className="text-[11px] text-slate-500 dark:text-slate-300">
              {c.createdAtUtc ? new Date(c.createdAtUtc).toLocaleString() : ''}
            </p>
          </div>
        </div>
      </div>
      {editingId === c.id ? (
        <textarea
          rows={2}
          value={editDraft}
          onChange={(e) => setEditDraft(e.target.value)}
          className="mt-2 w-full rounded-md border border-brand-200 px-2 py-1.5 text-xs dark:border-brand-900 dark:bg-slate-900"
        />
      ) : (
        <p className="mt-2 text-xs text-slate-700 dark:text-slate-200">{decorateMentions(c.body)}</p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
        <button
          type="button"
          onClick={() =>
            setReplyDraftById((prev) => ({
              ...prev,
              [c.id]: prev[c.id] === undefined ? '' : undefined,
            }))
          }
          className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 text-slate-600 dark:border-brand-900 dark:text-slate-300"
        >
          <Reply size={11} /> {t('reply')}
        </button>
        {canEdit && editingId !== c.id && (
          <button
            type="button"
            onClick={() => {
              setEditingId(c.id);
              setEditDraft(c.body);
            }}
            className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 text-slate-600 dark:border-brand-900 dark:text-slate-300"
          >
            <Pencil size={11} /> {t('edit')}
          </button>
        )}
        {canEdit && editingId === c.id && (
          <button
            type="button"
            onClick={() => void onSaveEdit?.(c.id, editDraft)}
            className="rounded-md bg-brand-600 px-2 py-1 text-white"
          >
            {t('save')}
          </button>
        )}
        {canEdit && (
          <button
            type="button"
            onClick={() => void onDelete?.(c.id)}
            className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 text-slate-600 dark:border-brand-900 dark:text-slate-300"
          >
            <Trash2 size={11} /> {t('delete')}
          </button>
        )}
      </div>
      {replyDraftById[c.id] !== undefined && (
        <div className="mt-2 flex gap-2">
          <input
            value={replyDraftById[c.id] ?? ''}
            onChange={(e) => setReplyDraftById((prev) => ({ ...prev, [c.id]: e.target.value }))}
            className="flex-1 rounded-md border border-brand-200 px-2 py-1 text-xs outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-900"
            placeholder={t('writeReply')}
          />
          <button
            type="button"
            onClick={() =>
              void onReply?.({
                parentId: c.id,
                text: (replyDraftById[c.id] || '').trim(),
                reset: () =>
                  setReplyDraftById((prev) => {
                    const n = { ...prev };
                    delete n[c.id];
                    return n;
                  }),
              })}
            className="inline-flex items-center rounded-md bg-brand-500 px-2.5 py-1 text-xs font-semibold text-white"
          >
            <Send size={11} className="mr-1" /> {t('send')}
          </button>
        </div>
      )}
      {(c.replies || []).map((child) => (
        <CommentBranch
          key={child.id}
          c={child}
          depth={depth + 1}
          decorateMentions={decorateMentions}
          viewerUserId={viewerUserId}
          onReply={onReply}
          onSaveEdit={onSaveEdit}
          onDelete={onDelete}
          replyDraftById={replyDraftById}
          setReplyDraftById={setReplyDraftById}
          editingId={editingId}
          setEditingId={setEditingId}
          editDraft={editDraft}
          setEditDraft={setEditDraft}
          t={t}
        />
      ))}
    </div>
  );
}

export function TaskDetailDrawerApi({
  task,
  project,
  columns,
  categories,
  onClose,
  onUpdateTask,
  apiProjectId,
  onBoardRefresh,
  viewerUserId,
  SkeletonCard,
  taskFieldsReadOnly,
  pageMode = false,
  t,
}) {
  void categories; // reserved for parity with demo drawer props

  const ro = Boolean(taskFieldsReadOnly);

  const numericTaskId = Number(task.id);

  const [model, setModel] = useState(() => normalizeDetail(null, task));
  const modelRef = useRef(model);

  /** Local merge (instant); keeps modelRef aligned before network persist(). */
  const applyPartial = useCallback((partial = {}) => {
    const merged = { ...modelRef.current, ...partial };
    modelRef.current = merged;
    setModel(merged);
    return merged;
  }, []);

  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');
  const [saving, setSaving] = useState(false);

  const [draftSubtask, setDraftSubtask] = useState('');
  const [commentInput, setCommentInput] = useState('');
  /** @type {React.MutableRefObject<HTMLTextAreaElement | null>} */
  const commentTextAreaRef = useRef(null);
  const commentCursorRef = useRef(0);
  const [mentionMembers, setMentionMembers] = useState(
    /** @type {Array<{ id: string; name: string; email: string; memberUserId?: string }>} */ ([]),
  );
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const fileInputRef = useRef(null);
  const audioFileInputRef = useRef(null);
  const audioModalOpenRef = useRef(false);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [fileAccept, setFileAccept] = useState('*/*');

  const [audioModalOpen, setAudioModalOpen] = useState(false);
  const [audioRecState, setAudioRecState] = useState('idle');
  const [audioRecErr, setAudioRecErr] = useState('');
  const [audioRecSecs, setAudioRecSecs] = useState(0);

  const [replyDraftById, setReplyDraftById] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');

  const [meetingOpen, setMeetingOpen] = useState(false);
  const [meetingDraft, setMeetingDraft] = useState('');

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const rows = await apiListTeamMembers();
        if (dead) return;
        const mapped = Array.isArray(rows) ? rows.map(mapTeamMemberDtoToUi) : [];
        const byId = new Map();
        for (const m of mapped) {
          if (m?.id && !byId.has(m.id)) byId.set(m.id, m);
        }
        setMentionMembers([...byId.values()]);
      } catch {
        if (!dead) setMentionMembers([]);
      }
    })();
    return () => {
      dead = true;
    };
  }, [apiProjectId]);

  const mentionMatches = useMemo(() => {
    const q = mentionFilter.trim().toLowerCase();
    return mentionMembers
      .filter((m) => m.name && (!q || m.name.toLowerCase().includes(q)))
      .slice(0, 8);
  }, [mentionMembers, mentionFilter]);

  const persist = useCallback(
    async (partial = {}) => {
      if (ro) return;
      const merged = applyPartial(partial);
      setSaving(true);
      try {
        const dueIso = merged.dueDateLocal ? `${merged.dueDateLocal}T12:00:00.000Z` : null;
        await apiSaveWorkItem(apiProjectId, numericTaskId, {
          title: String(merged.title || '').trim(),
          description: merged.description ?? '',
          priority: String(merged.priority || 'Medium').trim(),
          columnId: Number(merged.columnId),
          dueDateUtc: dueIso,
          assigneeUserId: merged.assigneeUserId || '',
          meetingLink: merged.meetingLink || '',
        });

        setModel(modelRef.current);

        const attCount =
          merged.attachments && Array.isArray(merged.attachments) ? merged.attachments.length : 0;
        onUpdateTask(task.id, {
          title: merged.title,
          priority: merged.priority,
          columnId: String(merged.columnId),
          assigneeUserId: merged.assigneeUserId || '',
          dueDateIso: merged.dueDateLocal || '',
          description: merged.description ?? '',
          hasAttachment: attCount > 0,
        });

        if (typeof onBoardRefresh === 'function') await onBoardRefresh();

        try {
          const raw = await apiGetWorkItemDetail(apiProjectId, numericTaskId);
          const nm = normalizeDetail(raw, merged);
          modelRef.current = nm;
          setModel(nm);
          onUpdateTask(task.id, { taskNumber: nm.taskNumber });
        } catch {
          /* stale board still ok */
        }
      } catch (e) {
        window.alert(e instanceof Error ? e.message : String(e));
      } finally {
        setSaving(false);
      }
    },
    [apiProjectId, numericTaskId, applyPartial, onBoardRefresh, onUpdateTask, task.id, ro],
  );

  useEffect(() => {
    let dead = false;
    (async () => {
      setLoading(true);
      setLoadErr('');
      try {
        const raw = await apiGetWorkItemDetail(apiProjectId, numericTaskId);
        const nm = normalizeDetail(raw, task);
        modelRef.current = nm;
        if (!dead) setModel(nm);
      } catch (e) {
        if (!dead) setLoadErr(e instanceof Error ? e.message : String(e));
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => {
      dead = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial open + task identity
  }, [apiProjectId, numericTaskId, task.id]);

  useEffect(() => {
    audioModalOpenRef.current = audioModalOpen;
  }, [audioModalOpen]);

  useEffect(() => {
    if (audioRecState !== 'recording') return undefined;
    const id = setInterval(() => setAudioRecSecs((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, [audioRecState]);

  const closeAudioModal = useCallback(() => {
    audioModalOpenRef.current = false;
    setAudioModalOpen(false);
    setAudioRecState('idle');
    setAudioRecErr('');
    setAudioRecSecs(0);

    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;

    const mr = mediaRecorderRef.current;
    if (mr && (mr.state === 'recording' || mr.state === 'paused')) {
      mr.stop();
    } else {
      chunksRef.current = [];
      mediaRecorderRef.current = null;
    }
  }, []);

  const decorateMentions = (text) => {
    const s = String(text || '');
    const parts = s.split(/(@[^\n@]+)/g);
    return parts.map((part, index) =>
      part.startsWith('@') ? (
        <span key={`m-${index}-${part.slice(0, 24)}`} className="font-medium text-brand-600 dark:text-brand-300">
          {part}
        </span>
      ) : (
        <Fragment key={`t-${index}`}>{part}</Fragment>
      ),
    );
  };

  const onPickFiles = useCallback(async (files) => {
    if (ro) return;
    const raw = files?.[0];
    if (!raw) return;
    const file = normalizeUploadFile(raw);
    try {
      await apiUploadTaskAttachment(apiProjectId, numericTaskId, file);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (audioFileInputRef.current) audioFileInputRef.current.value = '';
      if (typeof onBoardRefresh === 'function') await onBoardRefresh();
      const rawDetail = await apiGetWorkItemDetail(apiProjectId, numericTaskId);
      const nm = normalizeDetail(rawDetail, task);
      modelRef.current = nm;
      setModel(nm);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : String(e));
    }
  }, [apiProjectId, numericTaskId, onBoardRefresh, task, ro]);

  const startVoiceRecording = useCallback(async () => {
    setAudioRecErr('');
    if (!navigator.mediaDevices?.getUserMedia) {
      setAudioRecErr(t('recordingNotSupported'));
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mime = pickRecordingMimeType();
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];

      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mr.onstop = () => {
        streamRef.current?.getTracks().forEach((tr) => tr.stop());
        streamRef.current = null;

        const stillOpen = audioModalOpenRef.current;
        const recorder = mediaRecorderRef.current;

        if (!stillOpen) {
          chunksRef.current = [];
          mediaRecorderRef.current = null;
          return;
        }

        const blobType = recorder?.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: blobType });
        chunksRef.current = [];
        mediaRecorderRef.current = null;

        if (blob.size === 0) {
          setAudioRecErr(t('emptyRecording'));
          return;
        }

        audioModalOpenRef.current = false;
        setAudioModalOpen(false);
        setAudioRecState('idle');
        setAudioRecSecs(0);

        const fname = `voice-${Date.now()}${extFromRecorderMime(blobType)}`;
        const recorded = new File([blob], fname, { type: blobType });
        void onPickFiles([recorded]);
      };

      mediaRecorderRef.current = mr;
      setAudioRecSecs(0);
      mr.start(250);
      setAudioRecState('recording');
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (/NotAllowed|Permission|denied/i.test(msg)) {
        setAudioRecErr(t('micDenied'));
      } else {
        setAudioRecErr(msg);
      }
    }
  }, [onPickFiles, t]);

  /** Stop recorder and finalize upload inside `mr.onstop` */
  const stopVoiceRecording = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (mr?.state === 'recording') mr.stop();
  }, []);

  const assigneeChoices = useMemo(() => {
    const unassigned = { id: '', name: t('unassigned') };
    // keyed by actual user-id when accepted, else by display name to dedupe
    const seen = new Map();
    for (const m of mentionMembers) {
      const uid = typeof m.memberUserId === 'string' ? m.memberUserId.trim() : '';
      const label = String(m.name || '').trim() || String(m.email || '').trim();
      if (!label) continue;
      // Use real userId when available; fall back to a pseudo-id so pending
      // members still appear (server will ignore an unrecognised id gracefully)
      const key = uid || `pending:${label}`;
      if (!seen.has(key)) seen.set(key, { id: uid, name: uid ? label : `${label} (Pending)` });
    }
    const roster = [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
    const out = [unassigned, ...roster];
    // Always include the current viewer (workspace owner / logged-in user)
    if (viewerUserId && !seen.has(viewerUserId)) {
      out.push({ id: viewerUserId, name: t('me') });
    }
    return out;
  }, [mentionMembers, viewerUserId, t]);

  return (
    <>
      {!pageMode && <div className="fixed inset-0 z-40 bg-slate-900/45" onClick={onClose} />}
      <aside className={pageMode
        ? "flex h-full w-full flex-col overflow-hidden bg-white dark:bg-slate-950"
        : "fixed inset-y-0 right-0 z-50 w-full overflow-hidden rounded-none border border-brand-200 bg-white shadow-2xl dark:border-brand-900 dark:bg-slate-950 lg:w-[80%] lg:rounded-l-2xl"}>
        <div className="flex h-full flex-col">
          <header className="flex items-center justify-between border-b border-brand-200 px-4 py-3 dark:border-brand-900">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              {(model.taskNumber || task.taskNumber) && (
                <span
                  className="shrink-0 rounded-md bg-brand-100 px-2 py-1 font-mono text-[11px] font-semibold text-brand-800 dark:bg-brand-900/40 dark:text-brand-200"
                  title={t('taskReference')}
                >
                  {model.taskNumber || task.taskNumber}
                </span>
              )}
              <input
                value={model.title}
                disabled={loading || ro}
                readOnly={ro}
                onChange={(e) => applyPartial({ title: e.target.value })}
                onBlur={() => void persist()}
                className="w-full rounded-lg border border-brand-200 bg-white px-3 py-1.5 text-sm font-semibold outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-900"
              />
              <select
                value={model.columnId}
                disabled={loading || ro}
                onChange={(e) => void persist({ columnId: e.target.value })}
                className="rounded-lg border border-brand-200 bg-white px-2 py-1.5 text-xs dark:border-brand-900 dark:bg-slate-900"
              >
                {columns.map((column) => (
                  <option key={column.id} value={column.id}>
                    {columnLabel(column.name, t)}
                  </option>
                ))}
              </select>
              <select
                value={model.priority}
                disabled={loading || ro}
                onChange={(e) => void persist({ priority: e.target.value })}
                className="rounded-lg border border-brand-200 bg-white px-2 py-1.5 text-xs dark:border-brand-900 dark:bg-slate-900"
              >
                {Object.keys(priorityUi).map((priority) => (
                  <option key={priority} value={priority}>
                    {priorityLabel(priority, t)}
                  </option>
                ))}
              </select>
            </div>
            {!pageMode && (
              <button
                type="button"
                onClick={onClose}
                className="ml-2 rounded-md p-1.5 text-slate-500 hover:bg-brand-100 dark:text-slate-300 dark:hover:bg-brand-900/20"
              >
                <X size={16} />
              </button>
            )}
          </header>

          {loading && <div className="px-4 py-2 text-[11px] text-slate-500 dark:text-slate-400">{t('loadingTask')}</div>}
          {!!loadErr && <div className="px-4 py-2 text-[11px] text-red-600 dark:text-red-400">{loadErr}</div>}

          <div className="grid min-h-0 flex-1 gap-0 lg:grid-cols-5">
            <div className="min-h-0 overflow-y-auto border-r border-brand-200 p-4 dark:border-brand-900 lg:col-span-3">
              <section>
                <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">{t('description')}</p>
                <TaskDescriptionEditor
                  value={model.description || ''}
                  disabled={loading || ro}
                  placeholder={t('taskDescriptionPlaceholder')}
                  onChange={(html) => applyPartial({ description: html })}
                  t={t}
                />
              </section>

              {!ro && (
                <section className="mt-4">
                  <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">{t('attachments')} / {t('uploads')}</p>
                  <input ref={fileInputRef} type="file" accept={fileAccept} className="hidden" onChange={(e) => void onPickFiles(e.target.files)} />
                  <input
                    ref={audioFileInputRef}
                    type="file"
                    accept="audio/mpeg,.mp3,audio/wav,audio/x-wav,audio/webm,audio/mp4,audio/x-m4a,.m4a,.aac,audio/aac,.ogg,audio/ogg"
                    className="hidden"
                    onChange={(e) => {
                      void onPickFiles(e.target.files);
                      closeAudioModal();
                      if (audioFileInputRef.current) audioFileInputRef.current.value = '';
                    }}
                  />
                  <div className="flex flex-wrap gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        setFileAccept('image/png,image/jpeg,image/webp,image/gif,.webp');
                        queueMicrotask(() => fileInputRef.current?.click());
                      }}
                      className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 text-xs text-slate-600 dark:border-brand-900 dark:text-slate-300"
                    >
                      <Image size={12} /> {t('image')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setFileAccept(
                          'video/mp4,video/webm,video/quicktime,video/*,.mp4,.mov,.mkv,.webm,.m4v',
                        );
                        queueMicrotask(() => fileInputRef.current?.click());
                      }}
                      className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 text-xs text-slate-600 dark:border-brand-900 dark:text-slate-300"
                    >
                      <Video size={12} /> {t('video')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        audioModalOpenRef.current = true;
                        setAudioRecErr('');
                        setAudioModalOpen(true);
                      }}
                      className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 text-xs text-slate-600 dark:border-brand-900 dark:text-slate-300"
                    >
                      <Mic size={12} /> {t('audio')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setFileAccept('*/*');
                        queueMicrotask(() => fileInputRef.current?.click());
                      }}
                      className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 text-xs text-slate-600 dark:border-brand-900 dark:text-slate-300"
                    >
                      <FileText size={12} /> {t('uploadDocument')}
                    </button>
                  </div>
                </section>
              )}

              <section className="mt-4">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{t('subtasks')}</p>
                  {!ro && (
                    <div className="flex gap-1.5">
                      <button
                        type="button"
                        onClick={() => setDraftSubtask(sampleSubtaskTitle(model.title || task.title || ''))}
                        className="rounded-md border border-dashed border-brand-300 px-2 py-1 text-[11px] font-medium text-brand-800 dark:border-brand-700 dark:text-brand-200"
                      >
                        {t('fillSampleData')}
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          const title = draftSubtask.trim();
                          if (!title) return;
                          try {
                            await apiCreateTaskSubtask(apiProjectId, numericTaskId, title);
                            setDraftSubtask('');
                            const raw = await apiGetWorkItemDetail(apiProjectId, numericTaskId);
                            const nm = normalizeDetail(raw, task);
                            modelRef.current = nm;
                            setModel(nm);
                            if (typeof onBoardRefresh === 'function') await onBoardRefresh();
                          } catch (e) {
                            window.alert(e instanceof Error ? e.message : String(e));
                          }
                        }}
                        className="rounded-md bg-brand-500 px-2.5 py-1 text-xs font-semibold text-white"
                      >
                        {t('addSubtask')}
                      </button>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  {model.subtasks.map((subtask) => (
                    <div key={subtask.id} className="flex items-center justify-between rounded-lg border border-brand-200 px-2.5 py-2 dark:border-brand-900">
                      <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-200">
                        <input
                          type="checkbox"
                          disabled={ro}
                          checked={subtask.done}
                          onChange={async () => {
                            if (ro) return;
                            try {
                              await apiPatchTaskSubtask(apiProjectId, numericTaskId, Number(subtask.id), { done: !subtask.done });
                              const raw = await apiGetWorkItemDetail(apiProjectId, numericTaskId);
                              const nm = normalizeDetail(raw, task);
                              modelRef.current = nm;
                              setModel(nm);
                              if (typeof onBoardRefresh === 'function') await onBoardRefresh();
                            } catch (e) {
                              window.alert(e instanceof Error ? e.message : String(e));
                            }
                          }}
                        />
                        {subtask.title}
                      </label>
                    </div>
                  ))}
                  {!ro && (
                    <input
                      value={draftSubtask}
                      onChange={(event) => setDraftSubtask(event.target.value)}
                      placeholder={`+ ${t('addSubtask')}`}
                      className="w-full rounded-lg border border-brand-200 bg-white px-2.5 py-1.5 text-xs outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-900"
                    />
                  )}
                </div>
              </section>

              <section className="mt-4 rounded-xl border border-brand-200 p-3 dark:border-brand-900">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{t('comments')}</p>

                <div className="mt-3 space-y-3">
                  {loading ? (
                    SkeletonCard ? (
                      <>
                        <SkeletonCard />
                        <SkeletonCard />
                      </>
                    ) : (
                      <div className="text-xs text-slate-400 dark:text-slate-500 py-2">{t('loadingTask')}</div>
                    )
                  ) : (
                    model.commentsTree.map((root) => (
                      <CommentBranch
                        key={root.id}
                        c={root}
                        depth={0}
                        decorateMentions={decorateMentions}
                        viewerUserId={viewerUserId}
                        replyDraftById={replyDraftById}
                        setReplyDraftById={setReplyDraftById}
                        editingId={editingId}
                        setEditingId={setEditingId}
                        editDraft={editDraft}
                        setEditDraft={setEditDraft}
                        t={t}
                        onReply={async ({ parentId, text, reset }) => {
                          if (!text) return;
                          await apiCreateTaskComment(apiProjectId, numericTaskId, { body: text, parentCommentId: Number(parentId) });
                          reset?.();
                          const raw = await apiGetWorkItemDetail(apiProjectId, numericTaskId);
                          modelRef.current = normalizeDetail(raw, task);
                          setModel(modelRef.current);
                        }}
                        onSaveEdit={async (cid, bodyText) => {
                          await apiPatchTaskComment(apiProjectId, numericTaskId, Number(cid), bodyText);
                          setEditingId(null);
                          const raw = await apiGetWorkItemDetail(apiProjectId, numericTaskId);
                          modelRef.current = normalizeDetail(raw, task);
                          setModel(modelRef.current);
                        }}
                        onDelete={async (cid) => {
                          if (!window.confirm(t('deleteCommentConfirm'))) return;
                          await apiDeleteTaskComment(apiProjectId, numericTaskId, Number(cid));
                          const raw = await apiGetWorkItemDetail(apiProjectId, numericTaskId);
                          modelRef.current = normalizeDetail(raw, task);
                          setModel(modelRef.current);
                        }}
                      />
                    ))
                  )}
                </div>

                <div className="relative mt-3 rounded-lg border border-brand-200 p-2.5 dark:border-brand-900">
                  <textarea
                    ref={commentTextAreaRef}
                    rows={3}
                    value={commentInput}
                    onChange={(event) => {
                      const el = event.target;
                      const value = el.value;
                      const cursor = el.selectionStart;
                      commentCursorRef.current = cursor;
                      setCommentInput(value);
                      const before = value.slice(0, cursor);
                      const atIndex = before.lastIndexOf('@');
                      if (atIndex < 0) {
                        setMentionOpen(false);
                        setMentionFilter('');
                        return;
                      }
                      const afterAt = before.slice(atIndex + 1);
                      if (/[\s\n]/.test(afterAt)) {
                        setMentionOpen(false);
                        setMentionFilter('');
                        return;
                      }
                      setMentionOpen(true);
                      setMentionFilter(afterAt);
                    }}
                    onKeyUp={(event) => {
                      if (event.currentTarget === commentTextAreaRef.current) {
                        commentCursorRef.current = event.currentTarget.selectionStart;
                      }
                    }}
                    onClick={(event) => {
                      commentCursorRef.current = event.currentTarget.selectionStart;
                    }}
                    placeholder={t('writeCommentMention')}
                    className="w-full rounded-md border border-brand-200 px-2.5 py-1.5 text-xs outline-none ring-brand-300 focus:ring dark:border-brand-900 dark:bg-slate-900"
                  />
                  {mentionOpen && mentionMatches.length > 0 && (
                    <div className="absolute bottom-full left-2 z-20 mb-1 max-h-48 w-64 overflow-y-auto rounded-lg border border-brand-200 bg-white py-1 text-xs shadow-lg dark:border-brand-800 dark:bg-slate-900">
                      {mentionMatches.map((member) => (
                        <button
                          key={member.id}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            const pos = commentCursorRef.current;
                            const { text: next, cursor } = insertMentionAtCursor(commentInput, pos, member.name);
                            setCommentInput(next);
                            setMentionOpen(false);
                            setMentionFilter('');
                            requestAnimationFrame(() => {
                              const ta = commentTextAreaRef.current;
                              if (ta) {
                                ta.focus();
                                ta.setSelectionRange(cursor, cursor);
                                commentCursorRef.current = cursor;
                              }
                            });
                          }}
                          className="flex w-full flex-col px-2.5 py-1.5 text-left hover:bg-brand-50 dark:hover:bg-brand-950/50"
                        >
                          <span className="font-medium text-slate-800 dark:text-slate-100">{member.name}</span>
                          {member.email ? (
                            <span className="text-[10px] text-slate-500 dark:text-slate-400">{member.email}</span>
                          ) : null}
                        </button>
                      ))}
                    </div>
                  )}
                  {mentionOpen && mentionMembers.length === 0 && (
                    <p className="mt-2 text-[10px] text-slate-500 dark:text-slate-400">
                      {t('noTeamRosterMentions')}
                    </p>
                  )}
                  <div className="mt-2 flex justify-end gap-2">
                    <button
                      type="button"
                      disabled={loading}
                      onClick={async () => {
                        const text = commentInput.trim();
                        if (!text) return;
                        await apiCreateTaskComment(apiProjectId, numericTaskId, { body: text, parentCommentId: null });
                        setCommentInput('');
                        const raw = await apiGetWorkItemDetail(apiProjectId, numericTaskId);
                        modelRef.current = normalizeDetail(raw, task);
                        setModel(modelRef.current);
                      }}
                      className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                    >
                      {t('send')}
                    </button>
                  </div>
                </div>
              </section>
            </div>

            <div className="min-h-0 overflow-y-auto p-4 lg:col-span-2">
              <div className="space-y-3 text-xs">
                <label className="block">
                  <span className="mb-1 block text-slate-500 dark:text-slate-300">{t('assignee')}</span>
                  <select
                    disabled={loading || ro}
                    value={model.assigneeUserId}
                    onChange={(e) => void persist({ assigneeUserId: e.target.value })}
                    className="w-full rounded-lg border border-brand-200 bg-white px-2.5 py-1.5 dark:border-brand-900 dark:bg-slate-900"
                  >
                    {assigneeChoices.map((choice) => (
                      <option key={choice.id || 'none'} value={choice.id}>
                        {choice.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-[10px] text-slate-400">
                    {t('assigneeInviteHint')}
                  </p>
                </label>

                <label className="block">
                  <span className="mb-1 block text-slate-500 dark:text-slate-300">{t('dueDate')}</span>
                  <input
                    type="date"
                    disabled={loading || ro}
                    value={model.dueDateLocal}
                    onChange={(e) => void persist({ dueDateLocal: e.target.value })}
                    className="w-full rounded-lg border border-brand-200 bg-white px-2.5 py-1.5 dark:border-brand-900 dark:bg-slate-900"
                  />
                </label>

                <label className="block">
                  <span className="mb-1 block text-slate-500 dark:text-slate-300">{t('project')}</span>
                  <input readOnly value={project.name} className="w-full rounded-lg border border-brand-200 bg-slate-50 px-2.5 py-1.5 dark:border-brand-900 dark:bg-slate-900" />
                </label>

                <div>
                  <p className="mb-1 text-sm font-medium text-slate-700 dark:text-slate-200">{t('attachments')}</p>
                  <div className="space-y-3">
                    {model.attachments.length === 0 && (
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">{t('noFilesUploadedYet')}</p>
                    )}
                    {model.attachments.map((a) => (
                      <AttachmentPreview
                        key={a.id}
                        apiProjectId={apiProjectId}
                        taskId={numericTaskId}
                        attachment={a}
                        t={t}
                        onDelete={
                          ro
                            ? undefined
                            : async () => {
                                if (!window.confirm(`${t('delete')} "${a.originalFileName}"?`)) return;
                                await apiDeleteTaskAttachment(apiProjectId, numericTaskId, a.id);
                                const raw = await apiGetWorkItemDetail(apiProjectId, numericTaskId);
                                modelRef.current = normalizeDetail(raw, task);
                                setModel(modelRef.current);
                                if (typeof onBoardRefresh === 'function') await onBoardRefresh();
                              }
                        }
                      />
                    ))}
                  </div>
                </div>

                {!ro && (
                  <button
                    type="button"
                    onClick={() => {
                      setMeetingDraft(model.meetingLink || '');
                      setMeetingOpen(true);
                    }}
                    className="w-full rounded-lg border border-brand-300 px-3 py-1.5 text-xs font-medium text-brand-700 dark:border-brand-800 dark:text-brand-300"
                  >
                    {t('shareMeetingLink')}
                  </button>
                )}
              </div>
            </div>
          </div>

          {!ro && (
            <footer className="border-t border-brand-200 p-3 dark:border-brand-900">
              <button
                type="button"
                disabled={saving || loading}
                onClick={() => void persist()}
                className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-brand disabled:opacity-50"
              >
                {saving ? '…' : t('saveChanges')}
              </button>
            </footer>
          )}
        </div>
      </aside>

      {audioModalOpen && (
        <div
          className="fixed inset-0 z-[61] flex items-center justify-center bg-slate-900/50 p-4"
          onClick={() => closeAudioModal()}
        >
          <div
            className="w-full max-w-sm rounded-2xl border border-brand-200 bg-white p-5 shadow-2xl dark:border-brand-900 dark:bg-slate-950"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2">
              <h4 className="text-sm font-semibold text-slate-800 dark:text-white">
                {t('audioAttachmentTitle')}
              </h4>
              <button
                type="button"
                aria-label={t('close')}
                onClick={() => closeAudioModal()}
                className="rounded-lg p-1 text-slate-500 hover:bg-brand-100 dark:text-slate-400 dark:hover:bg-brand-900/30"
              >
                <X size={18} />
              </button>
            </div>
            <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
              {t('audioAttachmentSubtitle')}
            </p>

            {!!audioRecErr && (
              <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-[11px] text-red-700 dark:bg-red-900/25 dark:text-red-300">
                {audioRecErr}
              </p>
            )}

            {audioRecState === 'idle' ? (
              <div className="mt-4 flex flex-col gap-3">
                <button
                  type="button"
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-brand-200 py-3 text-sm font-medium text-slate-800 hover:bg-brand-50 dark:border-brand-800 dark:text-slate-100 dark:hover:bg-brand-950/50"
                  onClick={() => {
                    queueMicrotask(() => audioFileInputRef.current?.click());
                  }}
                >
                  <Upload size={18} strokeWidth={1.75} />
                  {t('uploadAudioFile')}
                </button>
                <button
                  type="button"
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-500 py-3 text-sm font-semibold text-white shadow-md shadow-brand"
                  onClick={() => void startVoiceRecording()}
                >
                  <Mic size={18} strokeWidth={1.75} />
                  {t('recordVoiceNote')}
                </button>
              </div>
            ) : (
              <div className="mt-5 flex flex-col items-center gap-4">
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-red-600/90 text-white shadow-lg shadow-red-500/30 animate-pulse">
                  <Mic size={36} strokeWidth={1.5} />
                </div>
                <p className="font-mono text-lg font-semibold tabular-nums text-slate-800 dark:text-slate-100">
                  {formatRecMmSs(audioRecSecs)}
                </p>
                <p className="text-center text-[11px] text-slate-500 dark:text-slate-400">
                  {t('recordingTapStop')}
                </p>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-xl bg-slate-800 px-5 py-2.5 text-sm font-semibold text-white dark:bg-slate-200 dark:text-slate-900"
                  onClick={() => stopVoiceRecording()}
                >
                  <Square size={14} fill="currentColor" />
                  {t('stopRecording')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {meetingOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-md rounded-xl border border-brand-200 bg-white p-4 shadow-xl dark:border-brand-900 dark:bg-slate-900">
            <h4 className="text-sm font-semibold text-slate-800 dark:text-white">{t('shareMeetingLink')}</h4>
            <input
              value={meetingDraft}
              onChange={(e) => setMeetingDraft(e.target.value)}
              placeholder={t('meetingLinkPlaceholder')}
              className="mt-3 w-full rounded-lg border border-brand-200 bg-white px-3 py-2 text-sm outline-none dark:border-brand-900 dark:bg-slate-950"
            />
            <div className="mt-3 flex flex-wrap justify-end gap-2">
              <button type="button" onClick={() => setMeetingDraft(sampleMeetingLink())} className="mr-auto rounded-lg border border-dashed border-brand-300 px-3 py-1.5 text-xs font-medium text-brand-800 dark:border-brand-700 dark:text-brand-200">
                {t('fillSampleData')}
              </button>
              <button type="button" onClick={() => setMeetingOpen(false)} className="rounded-lg border px-3 py-1.5 text-xs dark:border-brand-900">
                {t('cancel')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setMeetingOpen(false);
                  void persist({ meetingLink: meetingDraft.trim() });
                  setMeetingDraft('');
                }}
                className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white"
              >
                {t('saveLink')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
